import { MessageSquare, Wrench, GitCompareArrows, Cpu, FlaskConical, Rocket } from "lucide-react";
import Eyebrow from "@/components/zeblan/Eyebrow";
import Reveal from "@/components/zeblan/Reveal";

const steps = [
  { icon: MessageSquare, title: "The conversation", meta: "Account manager · ~1 hour", body: "We sit down to understand your needs, identify where AI can help, and calculate a clear ROI together, building a wish-list of what the software needs to do." },
  { icon: Wrench, title: "Technical review", meta: "Lead engineer · fixed fee · within a week", body: "Our engineers review the requirements to make sure they're practical, secure, and technically sound." },
  { icon: GitCompareArrows, title: "Alignment check", meta: "Account manager + lead engineer", body: "We review the technical plan with you, making sure we're completely aligned before any building begins." },
  { icon: Cpu, title: "Building the prototype", meta: "Dev team · MVP in 2–4 weeks", body: "We design the architecture and build a working Minimum Viable Product you can actually test, guided by a lead engineer and your account manager." },
  { icon: FlaskConical, title: "Test & tweak", meta: "Account manager + lead engineer", body: "We present the MVP to your team, take your feedback, make adjustments, and ensure it feels right." },
  { icon: Rocket, title: "Full rollout", meta: "Dev team · 4–8 weeks · then aftercare", body: "Once you're 100% happy, we implement the full software into your business and begin our aftercare support.", last: true },
];

export default function HowWeWork() {
  return (
    <section id="how-we-work" className="bg-cloud py-24 lg:py-32">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <Eyebrow>How We Work</Eyebrow>
          <h2 className="mt-6 text-navy font-extrabold tracking-tightish" style={{ fontSize: "clamp(30px,4vw,46px)" }}>
            A structured, collaborative process{" "}
            <span className="font-display italic font-medium text-blue">with no surprises</span>
          </h2>
          <p className="mt-5 text-slate text-[17px] leading-relaxed">
            From first conversation to full rollout, you always know exactly where things stand.
          </p>
        </Reveal>

        <div className="mt-16 relative max-w-3xl mx-auto">
          {/* Spine */}
          <div
            aria-hidden
            className="absolute left-[19px] md:left-[27px] top-0 bottom-0 w-[3px] rounded-full"
            style={{ background: "linear-gradient(to bottom, #2563EB 0%, #2563EB 30%, #36D1C5 100%)" }}
          />

          <ol className="space-y-10">
            {steps.map(({ icon: Icon, title, meta, body, last }, i) => (
              <Reveal as="li" key={title} delay={i * 80} className="relative pl-16 md:pl-20">
                <span
                  className={`absolute left-0 top-0 h-10 w-10 md:h-[54px] md:w-[54px] rounded-full grid place-items-center border-[3px] ${
                    last ? "bg-teal border-teal text-white" : "bg-white border-blue text-blue"
                  } shadow-[0_4px_14px_-6px_rgba(10,19,40,0.35)]`}
                  data-testid={`workflow-node-${i + 1}`}
                >
                  <Icon className="h-4 w-4 md:h-5 md:w-5" />
                </span>
                <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
                  <h3 className="text-navy text-[20px] font-bold">{title}</h3>
                  <span className="inline-block text-[11px] font-semibold uppercase tracking-[0.18em] bg-white border border-mist text-steel rounded-full px-3 py-1">
                    {meta}
                  </span>
                </div>
                <p className="mt-3 text-slate text-[15px] leading-relaxed max-w-2xl">{body}</p>
              </Reveal>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}
